/**
 * Login form hook for Aegix One.
 *
 * ADR-118 #18: wraps the lib's `useAuthActions().login`, which throws
 * `AuthError` with `kind: 'requires_verification'` on the
 * pending-verification path. The catch branch discriminates so the UI
 * can route to the verification screen.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuthActions,
  AuthError,
  type AllauthUser,
} from '@aegix/auth-identity';

export function useLoginForm() {
  const { t } = useTranslation();
  const { login } = useAuthActions<AllauthUser>();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (values: {
    email: string;
    password: string;
  }) => {
    setError('');
    setLoading(true);
    try {
      await login({ email: values.email, password: values.password });
      // Success — provider has dispatched login_success; App.tsx will
      // re-render with the authenticated routes.
    } catch (err) {
      if (err instanceof AuthError && err.kind === 'requires_verification') {
        navigate('/verify-email', { state: { email: values.email } });
        return;
      }
      setError(
        err instanceof Error ? err.message : t('auth.loginFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  return { error, setError, loading, showPassword, setShowPassword, handleSubmit };
}
